import { Popper, Tooltip, TooltipProps, makeStyles } from '@material-ui/core';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { withThemeNext } from '../layout/theme-next';
import useDeferredStyles from './OverflowTooltip.styles';

const INTERACTIVE_LEAVE_DELAY_MS = 200;

const SINGLE_LINE_STYLE: React.CSSProperties = {
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

interface Props extends Omit<TooltipProps, 'children' | 'title'> {
  title?: TooltipProps['title']
  maxLines?: number
  wrapperClassName?: string
  forceTooltip?: boolean
  children?: React.ReactNode
  deferred?: boolean
}

const useImmediateStyles = makeStyles({
  wrapper: {
    maxWidth: '100%',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  multilineWrapper: ({ maxLines = 2 }: Partial<Props>) => ({
    maxWidth: '100%',
    display: '-webkit-box',
    overflow: 'hidden',
    '-webkit-line-clamp': maxLines,
    '-webkit-box-orient': 'vertical',
  }),
});

const isOverflowing = (element: HTMLElement, maxLines: number): boolean => (
  maxLines > 1
    ? element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth
    : element.scrollWidth > element.clientWidth
);

const hasTooltipTitle = (title: Props['title']): title is NonNullable<Props['title']> => (
  title !== '' && title != null && title !== false
);

type TooltipEvent = Parameters<NonNullable<TooltipProps['onOpen']>>[0];

interface OverflowTooltipPopperProps {
  anchorEl: HTMLElement
  title: TooltipProps['title']
  placement: TooltipProps['placement']
  arrow: boolean
  interactive: boolean
  classes?: TooltipProps['classes']
  onMouseEnter: () => void
  onMouseLeave: (event: React.MouseEvent<HTMLElement>) => void
}

const OverflowTooltipPopper: React.FC<OverflowTooltipPopperProps> = ({
  anchorEl,
  title,
  placement,
  arrow,
  interactive,
  classes,
  onMouseEnter,
  onMouseLeave,
}) => {
  const styles = useDeferredStyles();
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement={placement}
      className={cx(styles.popper, { [styles.popperInteractive]: interactive })}
      modifiers={{
        offset: { offset: '0, 8' },
        arrow: {
          enabled: arrow && Boolean(arrowEl),
          element: arrowEl,
        },
      }}
    >
      <div
        className={cx(styles.tooltip, classes?.tooltip)}
        onMouseEnter={interactive ? onMouseEnter : undefined}
        onMouseLeave={interactive ? onMouseLeave : undefined}
      >
        {title}
        {arrow && (
          <span
            ref={setArrowEl}
            className={cx(styles.arrow, classes?.arrow)}
          />
        )}
      </div>
    </Popper>
  );
};

const OverflowTooltipImmediate: React.FC<Omit<Props, 'deferred'>> = ({
  title,
  children,
  maxLines = 1,
  forceTooltip,
  onClose,
  onOpen,
  open: openProp,
  wrapperClassName,
  ...props
}) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isTooltipEnabled, setIsTooltipEnabled] = React.useState(Boolean(forceTooltip));
  const classes = useImmediateStyles({ maxLines });

  const checkOverflow = React.useCallback(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return false;
    }

    return isOverflowing(wrapper, maxLines);
  }, [maxLines]);

  const handleOpen = React.useCallback((event: TooltipEvent) => {
    const shouldOpen = Boolean(forceTooltip) || checkOverflow();

    setIsTooltipEnabled(shouldOpen);

    if (shouldOpen) {
      setIsOpen(true);
      onOpen?.(event);
    } else {
      setIsOpen(false);
    }
  }, [checkOverflow, forceTooltip, onOpen]);

  const handleClose = React.useCallback((event: TooltipEvent) => {
    setIsOpen(false);
    onClose?.(event);
  }, [onClose]);

  const isEnabled = Boolean(forceTooltip) || isTooltipEnabled;
  const tooltipOpen = (openProp ?? isOpen) && isEnabled;

  return (
    <Tooltip
      {...props}
      onClose={handleClose}
      onOpen={handleOpen}
      open={tooltipOpen}
      title={isEnabled ? title ?? '' : ''}
    >
      <div
        className={cx(
          wrapperClassName,
          {
            [classes.multilineWrapper]: maxLines > 1,
            [classes.wrapper]: maxLines <= 1,
          },
        )}
        ref={wrapperRef}
      >
        {children}
      </div>
    </Tooltip>
  );
};

const ThemedOverflowTooltipImmediate = withThemeNext(OverflowTooltipImmediate);

const OverflowTooltipDeferred: React.FC<Omit<Props, 'deferred'>> = ({
  title,
  children,
  maxLines = 1,
  forceTooltip = false,
  onClose,
  onOpen,
  open: openProp,
  wrapperClassName,
  placement = 'top',
  arrow = false,
  interactive = false,
  classes,
  className,
  onClick,
  style,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number>();
  const [open, setOpen] = useState(false);
  const isOpen = openProp ?? open;

  const clearHideTimer = useCallback(() => {
    if (hideTimer.current !== undefined) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = undefined;
    }
  }, []);

  useEffect(() => clearHideTimer, [clearHideTimer]);

  const closeTooltip = useCallback((event?: TooltipEvent) => {
    clearHideTimer();
    setOpen(false);
    if (event) {
      onClose?.(event);
    }
  }, [clearHideTimer, onClose]);

  const handleMouseEnter = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    clearHideTimer();
    if (!hasTooltipTitle(title) || !wrapperRef.current) {
      return;
    }

    if (!forceTooltip && !isOverflowing(wrapperRef.current, maxLines)) {
      return;
    }

    if (!open) {
      onOpen?.(event as unknown as TooltipEvent);
    }
    setOpen(true);
  }, [clearHideTimer, forceTooltip, maxLines, onOpen, open, title]);

  const handlePopperEnter = useCallback(() => {
    clearHideTimer();
  }, [clearHideTimer]);

  const handleMouseLeave = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const closeEvent = event as unknown as TooltipEvent;
    if (!interactive) {
      closeTooltip(closeEvent);
      return;
    }

    clearHideTimer();
    hideTimer.current = window.setTimeout(() => closeTooltip(closeEvent), INTERACTIVE_LEAVE_DELAY_MS);
  }, [clearHideTimer, closeTooltip, interactive]);

  const wrapperStyle: React.CSSProperties = maxLines > 1
    ? {
      maxWidth: '100%',
      display: '-webkit-box',
      overflow: 'hidden',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      ...style,
    }
    : { ...SINGLE_LINE_STYLE, ...style };

  return (
    <>
      <div
        ref={wrapperRef}
        className={cx(wrapperClassName, className)}
        style={wrapperStyle}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {isOpen && wrapperRef.current && hasTooltipTitle(title) && (
        <OverflowTooltipPopper
          anchorEl={wrapperRef.current}
          title={title}
          placement={placement}
          arrow={Boolean(arrow)}
          interactive={Boolean(interactive)}
          classes={classes}
          onMouseEnter={handlePopperEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}
    </>
  );
};

const OverflowTooltip: React.FC<Props> = ({ deferred = false, ...props }) => (
  deferred
    ? <OverflowTooltipDeferred {...props} />
    : <ThemedOverflowTooltipImmediate {...props} />
);

export default React.memo(OverflowTooltip);
